import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { LoaderCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AuthLayout, FormError } from "@/components/auth/AuthLayout";
import { CaptchaField } from "@/components/auth/CaptchaField";
import { GoogleButton, OrDivider } from "@/components/auth/GoogleButton";
import { PasswordInput } from "@/components/auth/PasswordInput";
import { FormField, inputClass } from "@/components/form/FormField";
import { useAuth } from "@/context/AuthContext";
import { useCaptcha } from "@/hooks/useCaptcha";
import { formatApiError } from "@/lib/api";

const destination = (user, from) => (user.role === "admin" ? "/admin" : from && from !== "/admin" ? from : "/");

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const captcha = useCaptcha();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to={destination(user, location.state?.from)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!identifier.trim() || !password || !captcha.answer.trim()) return setError("Lengkapi username/email, password, dan kode captcha.");
    setError("");
    setLoading(true);
    try {
      const u = await login({ identifier: identifier.trim(), password, ...captcha.payload });
      toast.success(`Selamat datang, ${u.name}`);
      navigate(destination(u, location.state?.from), { replace: true });
    } catch (err) {
      setError(formatApiError(err, "Login gagal. Silakan coba lagi."));
      captcha.refresh();
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Masuk ke Akun" description="Untuk warga dan Pengurus RT. Gunakan username atau email Anda." testId="login-page">
      <form onSubmit={submit} className="space-y-4" noValidate data-testid="login-form">
        <FormField id="identifier" label="Username atau Email" required>
          <Input id="identifier" autoComplete="username" value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder="contoh: budi.merak atau budi@email.com" className={inputClass(false)} data-testid="login-identifier-input" />
        </FormField>
        <FormField id="password" label="Password" required>
          <PasswordInput id="password" value={password} onChange={setPassword} placeholder="Masukkan password" autoComplete="current-password" testId="login-password-input" />
        </FormField>
        <CaptchaField captcha={captcha} />
        <FormError message={error} testId="login-error" />
        <Button type="submit" disabled={loading} className="h-11 w-full rounded-xl bg-emerald-600 font-semibold hover:bg-emerald-700" data-testid="login-submit-btn">
          {loading && <LoaderCircle className="h-4 w-4 animate-spin" />} Masuk
        </Button>
      </form>
      <OrDivider />
      <GoogleButton />
      <p className="text-center text-sm text-slate-500">
        Belum punya akun warga?{" "}
        <Link to="/daftar" className="font-semibold text-emerald-700 hover:underline" data-testid="go-register-link">Daftar sekarang</Link>
      </p>
    </AuthLayout>
  );
}
